import { atom } from 'jotai';
import type { EmployeeAndSubcontractorUser } from 'model/User/types';

import type {
  InternalNote,
  MachinePlan,
  UserPlan,
} from 'model/ResourcePlanning/types';

export const projectAtom = atom('');

type UsersAtom = {
  usersById:
    | {
        [key: string]: EmployeeAndSubcontractorUser;
      }
    | undefined;
  soonToBeOrActiveUsers: EmployeeAndSubcontractorUser[] | undefined;
  soonToBeOrActiveUsersById:
    | {
        [key: string]: EmployeeAndSubcontractorUser;
      }
    | undefined;
  isFetchingUsers: boolean;
};
export const usersAtom = atom<UsersAtom>({
  usersById: undefined,
  soonToBeOrActiveUsers: undefined,
  soonToBeOrActiveUsersById: undefined,
  isFetchingUsers: true,
});

export const taskFilterAtom = atom<string[]>([]);

export const onlyUnplannedAtom = atom(false);

type PlanSelection = UserPlan[];
export const planSelectionAtom = atom<PlanSelection>([]);

type MachinePlanSelection = MachinePlan[];
export const machinePlanSelectionAtom = atom<MachinePlanSelection>([]);

interface PlanPopoverProps {
  open: boolean;
  plan: UserPlan | null;
  popoverAnchor: React.RefObject<HTMLSpanElement> | null;
  plannedIds: string[];
}
export const planPopoverAtom = atom<PlanPopoverProps>({
  open: false,
  plan: null,
  popoverAnchor: null,
  plannedIds: [],
});

interface MachinePlanPopoverProps {
  open: boolean;
  plan: MachinePlan | null;
  popoverAnchor: React.RefObject<HTMLSpanElement> | null;
  plannedIds: string[];
}
export const machinePlanPopoverAtom = atom<MachinePlanPopoverProps>({
  open: false,
  plan: null,
  popoverAnchor: null,
  plannedIds: [],
});

interface NoteDialogProps {
  open: boolean;
  date: Date | null;
  isUserNote: boolean;
  entityId: string | null;
  entityName: string | null;
  note?: InternalNote;
}
export const noteDialogAtom = atom<NoteDialogProps>({
  open: false,
  date: null,
  isUserNote: true,
  entityId: null,
  entityName: null,
});

export interface SinglePlanDialogProps {
  open: boolean;
  isUserPlan: boolean;
  plan: UserPlan | MachinePlan | null;
  toEdit: 'single' | 'future' | 'all';
  entityId: string;
  entityName: string;
  date: Date;
  plannedIds: string[];
}
export const singlePlanDialogAtom = atom<SinglePlanDialogProps>({
  open: false,
  isUserPlan: true,
  plan: null,
  toEdit: 'single',
  entityId: '',
  entityName: '',
  date: new Date(),
  plannedIds: [],
});

type MultiPlanDialogProps = {
  open: boolean;
} & (
  | { editingUserPlans: true; plans: UserPlan[]; machinePlans?: never }
  | { editingUserPlans: false; plans?: never; machinePlans: MachinePlan[] }
);
export const multiPlanDialogAtom = atom<MultiPlanDialogProps>({
  open: false,
  editingUserPlans: true,
  plans: [],
});

interface MoveSinglePlanDialogProps {
  open: boolean;
  plan: UserPlan | MachinePlan | null;
  entityName: string;
}
export const moveSinglePlanDialogAtom = atom<MoveSinglePlanDialogProps>({
  open: false,
  plan: null,
  entityName: '',
});

interface MovePlansDialogProps {
  open: boolean;
  plans: UserPlan[];
  machinePlans: MachinePlan[];
}
export const movePlansDialogAtom = atom<MovePlansDialogProps>({
  open: false,
  plans: [],
  machinePlans: [],
});

type CopyPlansDialogProps = { open: boolean } & (
  | {
      isUserPlan: true;
      plans: UserPlan[] | null;
    }
  | {
      isUserPlan: false;
      plans: MachinePlan[] | null;
    }
);
export const copyPlansDialogAtom = atom<CopyPlansDialogProps>({
  open: false,
  isUserPlan: true,
  plans: null,
});

type DeletePlansDialogProps = { open: boolean } & (
  | {
      plan: UserPlan | MachinePlan;
      toDelete: 'future' | 'all';
      plans?: never;
      machinePlans?: never;
    }
  | {
      plan?: never;
      toDelete?: never;
      plans: UserPlan[];
      machinePlans: MachinePlan[];
    }
);
export const deletePlansDialogAtom = atom<DeletePlansDialogProps>({
  open: false,
  plans: [],
  machinePlans: [],
});
